import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  ReportField,
  ReportFlowPages,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportSignLine,
  ReportSigns,
  ReportTitleRow,
} from '../../../components/ui/Report';
import type { ReliefBoardResponse, ReliefBoardRow } from '../types';

function dash(v: React.ReactNode) {
  if (v == null || v === '') return '—';
  return v;
}

function fmtDate(v?: string | null) {
  return v ? formatDateGlobal(v) : '—';
}

function availableRange(row: ReliefBoardRow): string {
  if (!row.availableFrom && !row.availableTo) return '—';
  if (row.availableFrom && row.availableTo && row.availableFrom !== row.availableTo) {
    return `${fmtDate(row.availableFrom)} – ${fmtDate(row.availableTo)}`;
  }
  return fmtDate(row.availableFrom || row.availableTo);
}

function medicalLabel(row: ReliefBoardRow): string {
  const status = row.medicalStatus?.trim();
  const due = row.medicalNextDue ? fmtDate(row.medicalNextDue) : null;
  if (status && due) return `${status} · ${due}`;
  if (status) return status;
  if (due) return due;
  return '—';
}

function TableHead() {
  return (
    <table className="rb-print-table rb-print-table-head">
      <thead>
        <tr>
          <th className="rb-print-col-no">No.</th>
          <th className="rb-print-col-crew">Crew</th>
          <th className="rb-print-col-rank">Rank</th>
          <th className="rb-print-col-nat">Nat.</th>
          <th className="rb-print-col-age">Age</th>
          <th className="rb-print-col-avail">Available</th>
          <th className="rb-print-col-status">Status</th>
          <th className="rb-print-col-days">Days ash.</th>
          <th className="rb-print-col-vessel">Last vessel</th>
          <th className="rb-print-col-med">Medical</th>
          <th className="rb-print-col-certs">Certs</th>
          <th className="rb-print-col-nr">NR</th>
          <th className="rb-print-col-agent">Agent</th>
          <th className="rb-print-col-mobile">Mobile</th>
        </tr>
      </thead>
    </table>
  );
}

function LineRow({ row, index }: { row: ReliefBoardRow; index: number }) {
  const tone = row.notRecommended ? 'nr' : row.expiredCerts > 0 ? 'certs' : 'ok';
  return (
    <table className={`rb-print-table rb-print-row-table rb-print-row-${tone}`}>
      <tbody>
        <tr>
          <td className="rb-print-col-no">{index + 1}</td>
          <td className="rb-print-col-crew">
            <span className="rb-print-name">{row.name}</span>
            {row.crewRef ? <span className="rb-print-ref">{row.crewRef}</span> : null}
          </td>
          <td className="rb-print-col-rank">{dash(row.rank)}</td>
          <td className="rb-print-col-nat">{dash(row.nationality)}</td>
          <td className="rb-print-col-age">{row.age ?? '—'}</td>
          <td className="rb-print-col-avail">{availableRange(row)}</td>
          <td className="rb-print-col-status">{dash(row.availabilityStatus)}</td>
          <td className="rb-print-col-days">{row.daysAshore ?? '—'}</td>
          <td className="rb-print-col-vessel">
            {dash(row.lastVessel)}
            {row.lastRank ? <span className="rb-print-ref">{row.lastRank}</span> : null}
          </td>
          <td className="rb-print-col-med">{medicalLabel(row)}</td>
          <td className="rb-print-col-certs">
            {row.expiredCerts > 0 ? (
              <span className="rb-print-pill rb-print-pill-warn">{row.expiredCerts} exp.</span>
            ) : (
              '0'
            )}
          </td>
          <td className="rb-print-col-nr">
            {row.notRecommended ? (
              <span className="rb-print-pill rb-print-pill-danger">NR</span>
            ) : (
              '—'
            )}
          </td>
          <td className="rb-print-col-agent">{dash(row.manningAgent)}</td>
          <td className="rb-print-col-mobile">{dash(row.mobile)}</td>
        </tr>
      </tbody>
    </table>
  );
}

export type ReliefBoardReportProps = {
  data: ReliefBoardResponse;
  rows: ReliefBoardRow[];
  filterLabels: {
    rank?: string | null;
    tab?: string | null;
    search?: string | null;
  };
};

export const ReliefBoardReport: React.FC<ReliefBoardReportProps> = ({
  data,
  rows,
  filterLabels,
}) => {
  const summary = data.summary;

  const firstHeader = useMemo(
    () => (
      <div className="rb-print-flow-header">
        <div className="rb-print-accent" aria-hidden />
        <ReportHeader>
          <ReportTitleRow title="Relief board" formId="Shore pool" />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="Rank" value={dash(filterLabels.rank || 'All ranks')} />
              <ReportField label="Filter" value={dash(filterLabels.tab || 'All')} />
              <ReportField label="Search" value={dash(filterLabels.search || '—')} />
              <ReportField label="Printed" value={fmtDate(new Date().toISOString().slice(0, 10))} />
            </ReportMetaRow>
          </ReportMeta>
          <div className="rb-print-kpis">
            <div className="rb-print-kpi rb-print-kpi-total">
              <span className="rb-print-kpi-label">Total</span>
              <span className="rb-print-kpi-value">{summary.total}</span>
            </div>
            <div className="rb-print-kpi rb-print-kpi-available">
              <span className="rb-print-kpi-label">Available</span>
              <span className="rb-print-kpi-value">{summary.available}</span>
            </div>
            <div className="rb-print-kpi rb-print-kpi-nr">
              <span className="rb-print-kpi-label">Not recommended</span>
              <span className="rb-print-kpi-value">{summary.notRecommended}</span>
            </div>
            <div className="rb-print-kpi rb-print-kpi-shown">
              <span className="rb-print-kpi-label">Shown</span>
              <span className="rb-print-kpi-value">{rows.length}</span>
            </div>
          </div>
        </ReportHeader>
        <TableHead />
      </div>
    ),
    [filterLabels, rows.length, summary],
  );

  const continueHeader = useMemo(
    () => (
      <div className="rb-print-flow-header rb-print-flow-continue">
        <div className="rb-print-continue-bar">
          <span className="rb-print-continue-title">Relief board</span>
          <span className="rb-print-continue-hint">continued</span>
        </div>
        <TableHead />
      </div>
    ),
    [],
  );

  const lastFooter = useMemo(
    () => (
      <ReportFooter>
        <ReportSigns>
          <ReportSignLine label="Prepared by" />
          <ReportSignLine label="Date" />
        </ReportSigns>
        <ReportNote>Shore relief pool — filtered view as of print time.</ReportNote>
      </ReportFooter>
    ),
    [],
  );

  const { items, itemKeys } = useMemo(() => {
    if (rows.length === 0) {
      return {
        items: [
          <table key="empty" className="rb-print-table rb-print-row-table">
            <tbody>
              <tr>
                <td colSpan={14} className="rb-print-empty">
                  No relief board rows for these filters.
                </td>
              </tr>
            </tbody>
          </table>,
        ],
        itemKeys: ['empty'],
      };
    }
    return {
      items: rows.map((row, i) => <LineRow key={row.crewId} row={row} index={i} />),
      itemKeys: rows.map((row) => row.crewId),
    };
  }, [rows]);

  return (
    <ReportFlowPages
      className="relief-board-print"
      size="landscape"
      firstHeader={firstHeader}
      continueHeader={continueHeader}
      lastFooter={lastFooter}
      items={items}
      itemKeys={itemKeys}
    />
  );
};

export default ReliefBoardReport;
